import { alpha } from '@mui/material/styles';

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

function createGradient(color1: string, color2: string) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

interface GradientsPaletteOptions {
  primary: string;
  info: string;
  success: string;
  warning: string;
  error: string;
}

interface ChartPaletteOptions {
  violet: string[];
  blue: string[];
  green: string[];
  yellow: string[];
  red: string[];
}

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }

  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }

  interface PaletteColor {
    lighter: string;
    darker: string;
  }

  interface Palette {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
  }

  interface PaletteOptions {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
  }
}

declare module '@mui/material' {
  interface Color {
    0: string;
    500_8: string;
    500_12: string;
    500_16: string;
    500_24: string;
    500_32: string;
    500_48: string;
    500_56: string;
    500_80: string;
  }
}

// SETUP COLORS
const PRIMARY = {
  lighter: '#C8FACD',
  light: '#5BE584',
  main: '#00AB55',
  dark: '#007B55',
  darker: '#005249'
};
const SECONDARY = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#3366FF',
  dark: '#1939B7',
  darker: '#091A7A'
};
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A'
};
const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D'
};
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01'
};
const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E'
};

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8)
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main)
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4']
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48
  }
};

export const CURRENT_CHART_COLORS = [
  '#FF0000', // Agencies (Fixed)
  '#8B0000', // Corporates (Fixed)
  '#4B0000', // Corporates (Float)
  '#2F0000', // Foreign Debt (Float)
  '#006400', // Municipals (Fixed)
  '#008080', // SBA Structured (Fixed)
  '#00FFFF', // SBA Pools (Float)
  '#0000FF', // MBS (Fixed)
  '#ADD8E6', // MBS (Float)
  '#FFFFE0', // CMBS Structured (Float)
  '#00008B', // CMBS Pools (Fixed)
  '#FFFF00', // CMO (Fixed)
  '#FFFACD', // CMO (Float)
  '#D3D3D3', // Treasuries
  '#90EE90', // Municipals Taxable (Fixed)
  '#800080' // CD's (Fixed)
];

export const CURRENT_COLOR_MAP: Record<string, string> = {
  Treasuries: '#BFBFBF',
  'Agencies (Fixed)': '#FF0010',
  'Corporates (Fixed)': '#85003B',
  'Municipals Taxable (Fixed)': '#2BCE48',
  'Municipals (Fixed)': '#007D42',
  "CD's (Fixed)": '#6E00FF',
  'MBS (Fixed)': '#0052CC',
  'CMO (Fixed)': '#FFE100',
  'Corporates (Float)': '#85003B',
  'MBS (Float)': '#51ACFF',
  'CMO (Float)': '#FFF080',
  'Agencies (Float)': '#FF0010',
  'SBA Pools (Fixed)': '#00998F',
  'SBA Pools (Float)': '#5EF1F2',
  'Stock/Preferred (Fixed)': '#85003B',
  'Stock/Preferred (Float)': '#85003B',
  'CMBS Pools (Fixed)': '#0052CC',
  'CMBS Pools (Float)': '#51ACFF',
  'ABS (Fixed)': '#FFE100',
  'ABS (Float)': '#FFF080',
  'Municipals Taxable (Float)': '#2BCE48',
  'Municipals (Float)': '#007D42',
  "CD's (Float)": '#6E00FF',
  'Other (Fixed)': '#85003B',
  'Other (Float)': '#85003B',
  'Other Amortizing (Fixed)': '#85003B',
  'Other Amortizing (Float)': '#85003B',
  'Agency Discount Notes': '#FF0010',
  'SBA Structured (Fixed)': '#00998F',
  'SBA Structured (Float)': '#5EF1F2',
  'Muni Leases (Fixed)': '#007D42',
  'Muni Leases (Float)': '#007D42',
  'Muni Leases Taxable (Fixed)': '#2BCE48',
  'Muni Leases Taxable (Float)': '#2BCE48',
  'MBS Private (Fixed)': '#0052CC',
  'MBS Private (Float)': '#51ACFF',
  'CMO Private (Fixed)': '#FFE100',
  'CMO Private (Float)': '#FFF080',
  'CMBS Structured (Fixed)': '#FFE100',
  'CMBS Structured (Float)': '#FFF080',
  'Foreign Debt (Fixed)': '#85003B',
  'Foreign Debt (Float)': '#85003B',
  'NGN (Fixed)': '#FFE100',
  'NGN (Float)': '#FFF080',
  'Commercial Paper (Fixed)': '#85003B',
  'Commercial Paper (Float)': '#85003B',
  'Money Market (Fixed)': '#85003B',
  'Money Market (Float)': '#85003B'
};

export const MODERN_CHART_COLORS = [
  '#D1E8FF',
  '#89C4FF',
  '#42A0FF',
  '#007CFF',
  '#0061B9',
  '#004771',
  '#000566',
  '#002C2A',
  '#00121A',
  '#000566',
  '#0000CC',
  '#000080',
  '#000033',
  '#00000F',
  '#000006',
  '#0000B3'
];

export const MODERN_COLOR_MAP: Record<string, string> = {
  Treasuries: '#E6F3FF',
  'Agencies (Fixed)': '#D1E8FF',
  'Corporates (Fixed)': '#B9DCFF',
  'Municipals Taxable (Fixed)': '#A1D0FF',
  'Municipals (Fixed)': '#89C4FF',
  "CD's (Fixed)": '#71B8FF',
  'MBS (Fixed)': '#5AACFF',
  'CMO (Fixed)': '#42A0FF',
  'Corporates (Float)': '#2A94FF',
  'MBS (Float)': '#1288FF',
  'CMO (Float)': '#007CFF',
  'Agencies (Float)': '#0073E6',
  'SBA Pools (Fixed)': '#006AD1',
  'SBA Pools (Float)': '#0061B9',
  'Stock/Preferred (Fixed)': '#0058A1',
  'Stock/Preferred (Float)': '#005089',
  'CMBS Pools (Fixed)': '#004771',
  'CMBS Pools (Float)': '#003E5A',
  'ABS (Fixed)': '#003542',
  'ABS (Float)': '#002C2A',
  'Municipals Taxable (Float)': '#002312',
  'Municipals (Float)': '#001A00',
  "CD's (Float)": '#00121A',
  'Other (Fixed)': '#000D33',
  'Other (Float)': '#00094C',
  'Other Amortizing (Fixed)': '#000566',
  'Other Amortizing (Float)': '#000280',
  'Agency Discount Notes': '#000099',
  'SBA Structured (Fixed)': '#0000B3',
  'SBA Structured (Float)': '#0000CC',
  'Muni Leases (Fixed)': '#0000E6',
  'Muni Leases (Float)': '#0000FF',
  'Muni Leases Taxable (Fixed)': '#0000E6',
  'Muni Leases Taxable (Float)': '#0000CC',
  'MBS Private (Fixed)': '#0000B3',
  'MBS Private (Float)': '#000099',
  'CMO Private (Fixed)': '#000080',
  'CMO Private (Float)': '#000066',
  'CMBS Structured (Fixed)': '#00004C',
  'CMBS Structured (Float)': '#000033',
  'Foreign Debt (Fixed)': '#00001A',
  'Foreign Debt (Float)': '#000012',
  'NGN (Fixed)': '#00000F',
  'NGN (Float)': '#00000C',
  'Commercial Paper (Fixed)': '#000009',
  'Commercial Paper (Float)': '#000006',
  'Money Market (Fixed)': '#000003',
  'Money Market (Float)': '#000000'
};

export const SCENARIO_CHART_COLORS: Array<string> = ['#FF0010', '#2BCE48', '#0052CC'];

export const palette = {
  light: {
    ...COMMON,
    text: { primary: GREY[800], secondary: GREY[600], disabled: GREY[500] },
    background: { paper: '#fff', default: '#fff', neutral: GREY[200] },
    action: { active: GREY[600], ...COMMON.action }
  },
  dark: {
    ...COMMON,
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: { paper: GREY[800], default: GREY[900], neutral: GREY[500_16] },
    action: { active: GREY[500], ...COMMON.action }
  }
};
